import type { Dispatch, FC, SetStateAction } from 'react';
import React, { useCallback, useState } from 'react';
import { Checkbox } from '@ordr-as/umami-react/checkbox';
import type { CartAdditionOption } from '../../../../../types/cart';
import { useLocation } from '../../../../context/LocationContext';
import { isEqualOptionId } from '../../../../utils/cart';
import type { OptionDto, OptionGroupDto } from '../../../../../generated-api';
import { useCheckout } from '../../../../context/CheckoutContext';
import { FormattedMessage } from 'react-intl';

interface Props {
  optionGroup: OptionGroupDto;
  setOptions: Dispatch<SetStateAction<CartAdditionOption[]>>;
  defaultOptions: CartAdditionOption[];
}

const MultipleChoiceSelection: FC<Props> = ({ optionGroup, setOptions, defaultOptions }) => {
  const { formatCurrency } = useLocation();
  const { vatType } = useCheckout();
  const showSelectAll = optionGroup.options.length >= 3;
  const [selectedOptions, setSelectedOptions] = useState<string[]>(defaultOptions.map((opt) => opt.option.optionId));

  const createCartOption = useCallback(
    (option: OptionDto): CartAdditionOption => ({
      option,
      quantity: 1,
      type: optionGroup.productType,
      vatType,
    }),
    [optionGroup.productType, vatType]
  );

  const onSelect = useCallback(
    (options: OptionDto[], isSelected: boolean) => {
      const optionIds = options.map((opt) => opt.optionId);

      if (isSelected) {
        setSelectedOptions((prev) => [...prev, ...optionIds.filter((id) => !prev.includes(id))]);
        setOptions((cartOptions) => {
          const newOptions = options.filter(
            (option) => !cartOptions.some((cartOption) => isEqualOptionId(option)(cartOption))
          );
          return [...cartOptions, ...newOptions.map(createCartOption)];
        });
      } else {
        setSelectedOptions((prev) => prev.filter((id) => !optionIds.includes(id)));
        setOptions((cartOptions) =>
          cartOptions.filter((cartOption) => !options.some((option) => isEqualOptionId(option)(cartOption)))
        );
      }
    },
    [setOptions, setSelectedOptions, createCartOption]
  );

  return (
    <div className="divide-y divide-grey-medium">
      {showSelectAll && (
        <Checkbox
          className="py-4"
          onChange={(selected) => onSelect(optionGroup.options, selected)}
          isSelected={selectedOptions.length === optionGroup.options.length}
        >
          <span className="text-bold">
            <FormattedMessage id="generic.selectAll" />
          </span>
        </Checkbox>
      )}
      {optionGroup.options.map((option) => (
        <Checkbox
          className="py-4"
          key={option.optionId}
          value={option.optionId}
          onChange={(selected) => onSelect([option], selected)}
          isSelected={selectedOptions.includes(option.optionId)}
        >
          {option.name}
          {option.price > 0 && (
            <span className="ml-auto">
              +{' '}
              {option.takeawayPrice
                ? `${formatCurrency(option.price)} | ${formatCurrency(option.takeawayPrice)}`
                : formatCurrency(option.price)}
            </span>
          )}
        </Checkbox>
      ))}
    </div>
  );
};

export default MultipleChoiceSelection;
